import * as React from 'react';
import { format } from 'date-fns';
import {
  CalendarRange,
  Check,
  CircleDollarSign,
  Clock3,
  Coins,
  Folder,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { cn } from '@shared';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/Popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { DaySheetlyDate } from '@/components/fields/DaySheetlyDate';
import { DaySheetlyInput } from '@/components/fields/DaySheetlyInput';
import { Tooltip } from '@/components/ui/Tooltip';
import { parseLocalDate } from '@/utils/date';

export interface TaskProjectOption {
  id: string;
  name: string;
  color?: string | null;
  hourlyRate?: number | null;
  currency?: 'BRL' | 'USD';
}

export interface TaskMemberOption {
  id: string;
  name: string;
}

export type TaskControlId = 'project' | 'members' | 'hourly-rate' | 'duration' | 'dates';

interface TaskControlButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon: LucideIcon;
  label: string;
  visibleLabel: string;
  tooltip: string;
  testId: string;
  open?: boolean;
  onClick?: () => void;
  onKeyDown?: React.KeyboardEventHandler<HTMLButtonElement>;
  onEscape?: () => void;
  badge?: number;
  indicatorColor?: string | null;
  active?: boolean;
  pressed?: boolean;
  invalid?: boolean;
}

const TaskControlButton = React.forwardRef<HTMLButtonElement, TaskControlButtonProps>(function TaskControlButton({
  icon: Icon,
  label,
  visibleLabel,
  tooltip,
  testId,
  open,
  onClick,
  onKeyDown,
  onEscape,
  badge,
  indicatorColor,
  active,
  pressed,
  invalid,
  ...buttonProps
}, ref) {
  return (
    <Tooltip content={tooltip}>
      <Button
        ref={ref}
        {...buttonProps}
        type="button"
        variant="outline"
        size="sm"
        data-testid={testId}
        aria-label={label}
        aria-expanded={open}
        aria-pressed={pressed}
        aria-invalid={invalid ? 'true' : undefined}
        onClick={onClick}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && open) {
            event.preventDefault();
            event.stopPropagation();
            onEscape?.();
            return;
          }
          onKeyDown?.(event);
        }}
        className={cn(
          'relative h-11 w-full shrink-0 justify-start gap-2 rounded-md border-input bg-background px-3 text-xs transition-colors sm:h-10 sm:w-auto sm:min-w-11 sm:justify-center sm:gap-1 sm:px-1.5',
          active && 'border-primary bg-accent text-accent-foreground',
          invalid && 'border-destructive text-destructive',
        )}
      >
        <Icon className="h-4 w-4" aria-hidden="true" />
        <span>{visibleLabel}</span>
        {indicatorColor && (
          <span
            aria-hidden="true"
            className="absolute right-1 top-1 h-2 w-2 rounded-full border border-background"
            style={{ backgroundColor: indicatorColor }}
          />
        )}
        {badge !== undefined && (
          <span
            aria-hidden="true"
            className="absolute -right-1 -top-1 inline-flex min-h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold leading-4 text-primary-foreground"
          >
            {badge}
          </span>
        )}
      </Button>
    </Tooltip>
  );
});
TaskControlButton.displayName = 'TaskControlButton';

interface TaskProjectControlProps {
  value: string;
  projects: TaskProjectOption[];
  open: boolean;
  error?: string;
  trigger?: React.ReactElement<React.ButtonHTMLAttributes<HTMLButtonElement>>;
  onOpenChange: (open: boolean) => void;
  onChange: (projectId: string) => void;
}

export function TaskProjectControl({
  value,
  projects,
  open,
  error,
  trigger,
  onOpenChange,
  onChange,
}: TaskProjectControlProps) {
  const selectedProject = projects.find((project) => project.id === value);
  const label = `Project: ${selectedProject?.name ?? 'No project'}`;

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        {trigger ?? (
          <TaskControlButton
            icon={Folder}
            label={label}
            visibleLabel="Project"
            tooltip={label}
            testId="task-project-control"
            open={open}
            onEscape={() => onOpenChange(false)}
            active={Boolean(selectedProject)}
            indicatorColor={selectedProject?.color}
            invalid={Boolean(error)}
          />
        )}
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-64 p-2"
        aria-label="Project selector"
        data-testid="task-project-panel"
        onEscapeKeyDown={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onOpenChange(false);
        }}
      >
        <div className="mb-2 px-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Project
        </div>
        <div role="listbox" aria-label="Projects" className="space-y-1">
          <ProjectOptionButton
            name="No project"
            selected={!value}
            onClick={() => {
              onChange('');
              onOpenChange(false);
            }}
          />
          {projects.map((project) => (
            <ProjectOptionButton
              key={project.id}
              name={project.name}
              color={project.color}
              selected={project.id === value}
              onClick={() => {
                onChange(project.id);
                onOpenChange(false);
              }}
            />
          ))}
        </div>
        {error && <p className="mt-2 px-2 text-xs text-destructive">{error}</p>}
      </PopoverContent>
    </Popover>
  );
}

function ProjectOptionButton({
  name,
  color,
  selected,
  onClick,
}: {
  name: string;
  color?: string | null;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      onClick={onClick}
      className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm outline-none hover:bg-accent focus-visible:bg-accent focus-visible:ring-2 focus-visible:ring-ring"
    >
      <span
        aria-hidden="true"
        className={cn(
          'inline-flex h-4 w-4 items-center justify-center rounded-full border',
          selected ? 'border-primary bg-primary text-primary-foreground' : 'border-input',
        )}
      >
        {selected && <Check className="h-3 w-3" />}
      </span>
      {color && (
        <span
          aria-hidden="true"
          className="h-3 w-3 shrink-0 rounded-full border border-black/10"
          style={{ backgroundColor: color }}
        />
      )}
      <span className="truncate">{name}</span>
    </button>
  );
}

interface TaskMembersControlProps {
  value: string[];
  members: TaskMemberOption[];
  open: boolean;
  error?: string;
  trigger?: React.ReactElement<React.ButtonHTMLAttributes<HTMLButtonElement>>;
  onOpenChange: (open: boolean) => void;
  onToggle: (memberId: string) => void;
}

export function TaskMembersControl({
  value,
  members,
  open,
  error,
  trigger,
  onOpenChange,
  onToggle,
}: TaskMembersControlProps) {
  const selectedIds = new Set(value);
  const label = `Members: ${value.length} selected`;

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        {trigger ?? (
          <TaskControlButton
            icon={Users}
            label={label}
            visibleLabel="Members"
            tooltip={label}
            testId="task-members-control"
            open={open}
            onEscape={() => onOpenChange(false)}
            badge={value.length > 0 ? value.length : undefined}
            active={value.length > 0}
            invalid={Boolean(error)}
          />
        )}
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-64 p-2"
        aria-label="Member selector"
        data-testid="task-members-panel"
        onEscapeKeyDown={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onOpenChange(false);
        }}
      >
        <div className="mb-2 flex items-center justify-between px-2">
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Members
          </span>
          <span className="text-xs text-muted-foreground">{value.length}/2</span>
        </div>
        <div className="max-h-56 space-y-1 overflow-y-auto">
          {members.length === 0 ? (
            <p className="px-2 py-2 text-sm text-muted-foreground">No members available</p>
          ) : (
            members.map((member) => {
              const checked = selectedIds.has(member.id);
              const disabled = !checked && value.length >= 2;
              return (
                <button
                  key={member.id}
                  type="button"
                  role="checkbox"
                  aria-checked={checked}
                  aria-disabled={disabled}
                  disabled={disabled}
                  data-testid={`member-option-${member.id}`}
                  onClick={() => onToggle(member.id)}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm outline-none hover:bg-accent focus-visible:bg-accent focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      'inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border border-primary',
                      checked && 'bg-primary text-primary-foreground',
                    )}
                  >
                    {checked && <Check className="h-3.5 w-3.5" />}
                  </span>
                  <Users className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                  <span className="truncate">{member.name}</span>
                </button>
              );
            })
          )}
        </div>
        {error && <p className="mt-2 px-2 text-xs text-destructive">{error}</p>}
        <p className="mt-2 px-2 text-xs text-muted-foreground">Choose up to two members.</p>
      </PopoverContent>
    </Popover>
  );
}

interface TaskHourlyRateControlProps {
  value: number;
  currency: 'BRL' | 'USD';
  project?: TaskProjectOption;
  open: boolean;
  error?: string;
  trigger?: React.ReactElement<React.ButtonHTMLAttributes<HTMLButtonElement>>;
  onOpenChange: (open: boolean) => void;
  onRateChange: (value: number | null) => void;
  onCurrencyChange: (currency: 'BRL' | 'USD') => void;
  onUseProjectRate: () => void;
}

export function TaskHourlyRateControl({
  value,
  currency,
  project,
  open,
  error,
  trigger,
  onOpenChange,
  onRateChange,
  onCurrencyChange,
  onUseProjectRate,
}: TaskHourlyRateControlProps) {
  const label = `Hourly rate: ${currency} ${value.toFixed(2)}`;

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        {trigger ?? (
          <TaskControlButton
            icon={Coins}
            label={label}
            visibleLabel="Rate"
            tooltip={label}
            testId="task-hourly-rate-control"
            open={open}
            onEscape={() => onOpenChange(false)}
            active={value > 0}
            invalid={Boolean(error)}
          />
        )}
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-72 p-3"
        aria-label="Hourly rate editor"
        data-testid="task-hourly-rate-panel"
        onEscapeKeyDown={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onOpenChange(false);
        }}
      >
        <div className="mb-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Hourly rate
        </div>
        <div className="flex items-end gap-2">
          <DaySheetlyInput
            id="hourly-rate"
            label="Hourly rate"
            variant="price"
            priceLocale={currency === 'BRL' ? 'pt-BR' : 'en-US'}
            value={value}
            onValueChange={(next) =>
              onRateChange(typeof next === 'string' ? Number(next) : next)
            }
            error={error}
            className="min-w-0 flex-1"
          />
          <div className="w-24 shrink-0">
            <Label htmlFor="task-currency" className="sr-only">
              Currency
            </Label>
            <Select value={currency} onValueChange={(next) => onCurrencyChange(next as 'BRL' | 'USD')}>
              <SelectTrigger id="task-currency" aria-label="Currency">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="BRL">BRL</SelectItem>
                <SelectItem value="USD">USD</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        {project && (
          <Button
            type="button"
            variant="link"
            className="mt-2 h-auto px-0 text-xs"
            onClick={onUseProjectRate}
          >
            Use current project rate
          </Button>
        )}
        <p className="mt-2 text-xs text-muted-foreground">
          This task keeps its own rate. Changing the project rate later will not rewrite history.
        </p>
      </PopoverContent>
    </Popover>
  );
}

interface TaskDurationControlProps {
  value: string;
  open: boolean;
  error?: string;
  trigger?: React.ReactElement<React.ButtonHTMLAttributes<HTMLButtonElement>>;
  onOpenChange: (open: boolean) => void;
  onChange: (value: string) => void;
}

export function TaskDurationControl({
  value,
  open,
  error,
  trigger,
  onOpenChange,
  onChange,
}: TaskDurationControlProps) {
  const label = `Duration: ${value || 'not set'}`;

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        {trigger ?? (
          <TaskControlButton
            icon={Clock3}
            label={label}
            visibleLabel="Duration"
            tooltip={label}
            testId="task-duration-control"
            open={open}
            onEscape={() => onOpenChange(false)}
            active={Boolean(value)}
            invalid={Boolean(error)}
          />
        )}
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-56 p-3"
        aria-label="Duration editor"
        data-testid="task-duration-panel"
        onEscapeKeyDown={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onOpenChange(false);
        }}
      >
        <Label htmlFor="duration">Duration</Label>
        <Input
          id="duration"
          value={value}
          placeholder="6h"
          aria-invalid={error ? 'true' : 'false'}
          onChange={(event) => onChange(event.target.value)}
          className={cn('mt-2', error && 'border-destructive')}
        />
        {error && <p className="mt-1 text-xs text-destructive">{error}</p>}
        <p className="mt-2 text-xs text-muted-foreground">Examples: 1h, 1h 30m.</p>
      </PopoverContent>
    </Popover>
  );
}

interface TaskDateRangeControlProps {
  startDate: string;
  endDate?: string;
  open: boolean;
  error?: string;
  trigger?: React.ReactElement<React.ButtonHTMLAttributes<HTMLButtonElement>>;
  onOpenChange: (open: boolean) => void;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
}

export function TaskDateRangeControl({
  startDate,
  endDate,
  open,
  error,
  trigger,
  onOpenChange,
  onStartDateChange,
  onEndDateChange,
}: TaskDateRangeControlProps) {
  const label = `Dates: ${formatDateLabel(startDate, 'No start date')} to ${formatDateLabel(endDate, 'No end date')}`;

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        {trigger ?? (
          <TaskControlButton
            icon={CalendarRange}
            label={label}
            visibleLabel="Dates"
            tooltip={label}
            testId="task-dates-control"
            open={open}
            onEscape={() => onOpenChange(false)}
            active={Boolean(startDate || endDate)}
            invalid={Boolean(error)}
          />
        )}
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[min(32rem,calc(100vw-2rem))] p-3"
        aria-label="Date range editor"
        data-testid="task-dates-panel"
        onEscapeKeyDown={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onOpenChange(false);
        }}
      >
        <div className="mb-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Dates
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <DaySheetlyDate
            label="Start date"
            value={startDate || null}
            onChange={(date) => onStartDateChange(date || '')}
            placeholder="Start date"
            clearable={false}
            error={error}
            testId="task-start-date"
          />
          <DaySheetlyDate
            label="End date"
            value={endDate || null}
            onChange={(date) => onEndDateChange(date || '')}
            placeholder="End date"
            clearable
            testId="task-end-date"
          />
        </div>
        {error && <p className="mt-2 text-xs text-destructive">{error}</p>}
        <p className="mt-2 text-xs text-muted-foreground">Start date is required; end date is optional.</p>
      </PopoverContent>
    </Popover>
  );
}

function formatDateLabel(value: string | undefined, fallback: string) {
  if (!value) return fallback;
  try {
    return format(parseLocalDate(value), 'MMM d, yyyy');
  } catch {
    return fallback;
  }
}

interface TaskBillingControlProps {
  value: 'unbilled' | 'billed';
  trigger?: React.ReactElement<React.ButtonHTMLAttributes<HTMLButtonElement>>;
  onChange: (value: 'unbilled' | 'billed') => void;
}

export function TaskBillingControl({ value, trigger, onChange }: TaskBillingControlProps) {
  const billed = value === 'billed';
  const nextState = billed ? 'unbilled' : 'billed';
  const label = `Billing: ${billed ? 'Billed' : 'Unbilled'}. Click to mark ${nextState}`;
  const handleChange = () => onChange(billed ? 'unbilled' : 'billed');

  if (trigger) {
    return React.cloneElement(trigger, {
      'aria-label': label,
      'aria-pressed': billed,
      onClick: handleChange,
    });
  }

  return (
    <Tooltip content={billed ? 'Billed — click to mark unbilled' : 'Unbilled — click to mark billed'}>
      <Button
        type="button"
        variant="outline"
        size="sm"
        data-testid="task-billing-control"
        aria-label={label}
        aria-pressed={billed}
        onClick={handleChange}
        className={cn(
          'h-11 w-full shrink-0 justify-start gap-2 rounded-md border-input px-3 text-xs sm:h-10 sm:w-auto sm:min-w-11 sm:justify-center sm:gap-1 sm:px-1.5',
          billed && 'border-emerald-500/50 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-950/30 dark:text-emerald-300',
        )}
      >
        <CircleDollarSign className="h-4 w-4" aria-hidden="true" />
        <span>Billing</span>
      </Button>
    </Tooltip>
  );
}
